import {
  CircleMinus,
  Eye,
  EyeOff,
  Filter,
  Pencil,
  Share,
  Star,
  TriangleAlert,
} from 'lucide-react';
import { pathFinderActions } from './pathFinderSlice';
import { numberFormatter } from '../helpers/formatters';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { ReusableModal } from '../modals/ReusableModal';
import { CapacityComponentBulkHistory } from '../flows/CapacityComponentBulkHistory';
import { PointBadge } from '../helpers/PointBadge';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAllNodes,
  selectAllPoints,
  selectSortedTsps,
} from '../map/slice/mapSelectors';
import { useTheme, useTspFilter } from '../helpers/hooks';
import { mapActions, Path } from '../map/slice/mapSlice';
import { generateColor, getContrastColor } from '../utils/colors';
import {
  PartialRecord,
  PathSpec,
  PriceDefinitionSpec,
  PathReport,
  PathFlows,
  ResolvedContractSegmentSummary,
  SegmentContractOption,
} from '@/types';
import {
  useGetPriceDefinitionOptionsMutation,
  useGetSegmentContractOptionsMutation,
  useGetPathFlowsMutation,
  useGetWatchlistsQuery,
  useResolvePathSpecMutation,
} from '../services/edgeApi';
import { AddToWatchlistModal } from './AddToWatchlistModal';
import { useEnrichedPaths } from './usePathWatchlists';
import { useLocalStorage } from '../helpers/useLocalStorage';
// @ts-ignore
import './PathTable.css';
import { Input, UncontrolledTooltip } from 'reactstrap';
import { useFitMap } from '../plotter/usePanMap';
import toast from 'react-hot-toast';
import { tuplesToList } from '../utils/objectUtils';
import { MapEventListener, useMapContext } from '../map/context/MapContext';
import { NoticeRecordModal } from '../notices/NoticeRecordModal';
import { SegmentContractPickerModal } from './SegmentContractPickerModal';
import { PriceDefinitionPickerModal } from './PriceDefinitionPickerModal';
import { PathReportSelectionContext } from '../utils/pathReportState';
import {
  describeContractUsageAvailability,
  formatContractName,
  getContractUsageLimiterLabels,
} from './pathDisplayFormatters';
import { useAnalytics } from '../hooks/useAnalytics';
import { AnalyticsEvent } from '../analytics';

const pathEditSelectionKey = (path: PathReport) =>
  path.report_row_id ??
  [
    path.original_path_fingerprint,
    path.buy_asset_description ?? '',
    path.sell_asset_description ?? '',
  ].join('|');

const pathSpecFromReport = (path: PathReport): PathSpec =>
  path.path_spec ?? {
    segments: path.original_path_key.map(([rPointId, dPointId]) => ({
      r_point_id: rPointId,
      d_point_id: dPointId,
      contract_spec: { type: 'any' },
    })),
  };

const formatPoolWithAdder = (
  pool: string | null | undefined,
  adder: number | null | undefined,
) => {
  if (!pool) return 'N/A';
  if (!adder) return pool;
  const sign = adder > 0 ? '+' : '-';
  return `${pool} ${sign} ${Math.abs(adder)}`;
};

function AddToWatchlistButton({ path }: { path: PathReport }) {
  const [modalOpen, setModalOpen] = useState(false);
  const isInAnyWatchlist = Boolean(path.is_in_watchlist);
  const hasSimilarSavedPath = Boolean(path.has_similar_watchlist_path);
  const title = isInAnyWatchlist
    ? 'This exact path configuration is saved'
    : hasSimilarSavedPath
    ? 'A different configuration of this route is saved'
    : 'Add to watchlist';

  return (
    <>
      <button
        type="button"
        className={`btn btn-link border-0 p-0 d-inline-flex align-items-center justify-content-center lh-1 text-decoration-none ${
          isInAnyWatchlist
            ? 'text-warning'
            : hasSimilarSavedPath
            ? 'text-info'
            : 'text-secondary'
        }`}
        onClick={(e) => {
          e.stopPropagation();
          setModalOpen(true);
        }}
        title={title}
        aria-label={title}
      >
        <span className="position-relative d-inline-flex">
          <Star fill={isInAnyWatchlist ? 'currentColor' : 'none'} />
          {hasSimilarSavedPath && (
            <Pencil
              size={11}
              className="position-absolute bottom-0 end-0 bg-body rounded-circle"
              aria-hidden="true"
            />
          )}
        </span>
      </button>

      {modalOpen && (
        <AddToWatchlistModal
          path={path}
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}

function SharePathButton({ path }: { path: PathReport }) {
  const [copied, setCopied] = useState(false);

  const generateShareUrl = useCallback(() => {
    // Extract point IDs from path_elements to build the full sequence
    const pointIds: number[] = [];

    if (path.path_elements && Array.isArray(path.path_elements)) {
      for (const element of path.path_elements) {
        if (element.type === 'point') {
          pointIds.push(element.id);
        }
      }
    }

    // Fallback to r_point_id and d_point_id if no path_elements
    const sequence =
      pointIds.length >= 2
        ? pointIds.join(',')
        : [path.r_point_id, path.d_point_id].join(',');

    const baseUrl = window.location.origin;

    // Extract current scenario from URL (e.g., /scenarios/next_day/path-builder)
    const currentScenario = window.location.pathname.match(
      /\/scenarios\/([^\/]+)/,
    )?.[1];
    const scenarioId = path.scenario_id || currentScenario || 'next_day';

    return `${baseUrl}/scenarios/${scenarioId}/path-builder?sequence=${encodeURIComponent(
      sequence,
    )}`;
  }, [path]);

  const handleShare = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      const url = generateShareUrl();
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success('Link copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy:', err);
        toast.error('Failed to copy link');
      }
    },
    [generateShareUrl],
  );

  return (
    <button
      type="button"
      className="btn btn-link border-0 p-0 d-inline-flex align-items-center justify-content-center lh-1 text-decoration-none text-secondary"
      onClick={handleShare}
      title={copied ? 'Copied!' : 'Copy share link'}
    >
      <Share />
    </button>
  );
}

export type DynamicTableProps<T> = {
  data: T[];
  columns: {
    key: string;
    header: string | JSX.Element;
    render?: (row: T, rowIndex: number) => React.ReactNode;
    className?: string;
    thClassName?: string;
    thStyle?: React.CSSProperties;
    tdClassName?: string;
    tdStyle?: React.CSSProperties;
    style?: React.CSSProperties;
    width?: string;
  }[];
};

type ConstraintDisplayMode = 'key' | 'worst' | 'all' | 'none';
type ConstraintSortMode = 'path' | 'impact' | 'constraint_priority';

type ConstraintDisplaySettings = {
  displayMode: ConstraintDisplayMode;
  showAllNotices: boolean;
  sortMode: ConstraintSortMode;
};

/**
 * Constraints pivot table reused inside the expanded segment rows.
 */
export function PivotConstraintTable({
  constraints,
  constraintDisplaySettings,
  showAvoidConstraints = false,
  onSelectNotice,
  onShowAllConstraints,
  stickyHeaderTop,
  showSegment = false,
}: {
  constraints: any[];
  constraintDisplaySettings: ConstraintDisplaySettings;
  showAvoidConstraints?: boolean;
  onSelectNotice?: (noticeId: number) => void;
  onShowAllConstraints?: () => void;
  stickyHeaderTop?: number;
  showSegment?: boolean;
}) {
  const ALL_RANKS_MAX = 9999;
  const tooltipIdPrefix = useRef(
    `constraint-tooltip-${Math.random().toString(36).slice(2)}`,
  ).current;
  const { displayMode, showAllNotices, sortMode } = constraintDisplaySettings;
  const dispatch = useDispatch();
  const avoidConstraints = useSelector(
    (s: any) => (s as any).pathFinder?.avoidConstraints ?? [],
  );
  const analytics = useAnalytics();

  const isAvoided = (
    constraint_key?: string,
    path_rank?: number,
    mode: 'up_to' | 'all' = 'up_to',
  ) => {
    if (!constraint_key || !Number.isFinite(path_rank)) return false;
    if (mode === 'all') {
      return (avoidConstraints || []).some(
        (x: any) =>
          x?.constraint_key === constraint_key &&
          (x?.mode ?? 'up_to') === 'all',
      );
    }
    return (avoidConstraints || []).some(
      (x: any) =>
        x?.constraint_key === constraint_key &&
        (x?.mode ?? 'up_to') === 'up_to' &&
        typeof x?.max_rank === 'number' &&
        Number(path_rank) <= x.max_rank,
    );
  };

  const onToggle = (
    constraint_key: string,
    max_rank: number,
    label?: string,
    mode: 'up_to' | 'all' = 'up_to',
  ) => {
    if (!constraint_key || typeof max_rank !== 'number') return;

    if (mode === 'all') {
      const existingAll = (avoidConstraints || []).filter(
        (x: any) =>
          x?.constraint_key === constraint_key &&
          (x?.mode ?? 'up_to') === 'all',
      );

      if (existingAll.length > 0) {
        existingAll.forEach((m: any) => {
          dispatch(
            pathFinderActions.removeAvoidConstraint({
              constraint_key: String(m.constraint_key),
              max_rank: Number(m.max_rank),
              mode: 'all',
            }),
          );
        });
        return;
      }

      dispatch(
        pathFinderActions.removeAvoidConstraint({
          constraint_key,
          mode: 'up_to',
        }),
      );
      dispatch(
        pathFinderActions.addAvoidConstraint({
          constraint_key,
          max_rank: ALL_RANKS_MAX,
          label,
          mode: 'all',
        }),
      );
      analytics.logEvent(AnalyticsEvent.AVOIDED_CONSTRAINT, {
        constraint_key,
        mode: 'all',
      });
      return;
    }

    const existingAll = (avoidConstraints || []).filter(
      (x: any) =>
        x?.constraint_key === constraint_key && (x?.mode ?? 'up_to') === 'all',
    );
    if (existingAll.length > 0) {
      existingAll.forEach((m: any) => {
        dispatch(
          pathFinderActions.removeAvoidConstraint({
            constraint_key: String(m.constraint_key),
            max_rank: Number(m.max_rank),
            mode: 'all',
          }),
        );
      });
    }

    const matching = (avoidConstraints || []).filter(
      (x: any) =>
        x?.constraint_key === constraint_key &&
        (x?.mode ?? 'up_to') === 'up_to' &&
        typeof x?.max_rank === 'number' &&
        x.max_rank >= max_rank,
    );

    if (matching.length > 0) {
      matching.forEach((m: any) => {
        dispatch(
          pathFinderActions.removeAvoidConstraint({
            constraint_key: String(m.constraint_key),
            max_rank: Number(m.max_rank),
            mode: 'up_to',
          }),
        );
      });
      return;
    }

    dispatch(
      pathFinderActions.addAvoidConstraint({
        constraint_key,
        max_rank,
        label,
        mode: 'up_to',
      }),
    );
    analytics.logEvent(AnalyticsEvent.AVOIDED_CONSTRAINT, {
      constraint_key,
      mode: 'up_to',
    });
  };
  if (!constraints || constraints.length === 0)
    return <div>No constraints</div>;

  const dates = Array.from(
    new Set(constraints.map((c) => c.constraint.gas_date)),
  );

  function makeConstraintKey(row: any) {
    const capacityComponentId = row.constraint.capacity_component_id;
    let key: string;
    if (capacityComponentId != null) {
      key = `capacity-component-${capacityComponentId}`;
    } else {
      key = [
        'other',
        row.constraint.description,
        row.constraint.type,
        row.constraint.flow_direction,
      ].join(' - ');
    }

    return row.segment_label ? `${row.segment_label} - ${key}` : key;
  }

  const pivoted: any[] = [];
  for (const row of constraints) {
    const key = makeConstraintKey(row);
    let entry = pivoted.find((x) => x._key === key);

    if (!entry) {
      entry = {
        _key: key,
        description:
          row.constraint.capacity_component_label ??
          `Other: ${row.constraint.description}`,
        segment_label: row.segment_label,
        resolved_path_priority: row.resolved_path_priority,
        effective_max_volume: row.max_volume,
        byDate: {},
      };
      pivoted.push(entry);
    } else {
      if (row.max_volume != null) {
        entry.effective_max_volume =
          entry.effective_max_volume == null
            ? row.max_volume
            : Math.min(entry.effective_max_volume, row.max_volume);
      }
    }

    const constraintsForDate =
      entry.byDate[row.constraint.gas_date] ??
      (entry.byDate[row.constraint.gas_date] = []);
    constraintsForDate.push({
      resolved_constraint_priority: row.resolved_constraint_priority,
      ec_max_volume: row.max_volume,
      max_volume: row.constraint.max_volume,
      is_applicable: row.is_applicable,
      severity: row.severity,
      source_id: row.constraint.source_id,
      source_type: row.constraint.source_type,
      // for per-date add action (use path rank from this eval)
      path_rank: row.resolved_path_priority_rank,
      constraint_priority_rank: row.resolved_constraint_priority_rank,
      constraint_key:
        row?.constraint?.constraint_key ?? row?.constraint?.component_key,
      description: row.constraint.description,
    });
  }

  const compareConstraints = (left: any, right: any) => {
    const leftVolume = Number(left.ec_max_volume);
    const rightVolume = Number(right.ec_max_volume);
    const leftHasVolume = Number.isFinite(leftVolume);
    const rightHasVolume = Number.isFinite(rightVolume);

    if (leftHasVolume !== rightHasVolume) return leftHasVolume ? -1 : 1;
    if (leftHasVolume && leftVolume !== rightVolume) {
      return leftVolume - rightVolume;
    }

    return (
      Number(right.constraint_priority_rank ?? -Infinity) -
      Number(left.constraint_priority_rank ?? -Infinity)
    );
  };

  const displayedConstraints = (constraintsForDate: any[]) => {
    if (displayMode === 'none') return [];
    if (displayMode === 'all') return constraintsForDate;
    if (displayMode === 'worst') {
      return constraintsForDate.filter(
        (constraint) => constraint.severity === 2,
      );
    }

    const applicable = constraintsForDate.filter(
      (constraint) => constraint.is_applicable,
    );

    const keyConstraint = applicable.length
      ? [...applicable].sort(compareConstraints).slice(0, 1)
      : [...constraintsForDate]
          .filter((constraint) => !constraint.is_applicable)
          .sort(
            (left, right) =>
              Number(right.constraint_priority_rank ?? -Infinity) -
              Number(left.constraint_priority_rank ?? -Infinity),
          )
          .slice(0, 1);
    if (!showAllNotices) return keyConstraint;

    return constraintsForDate.filter(
      (constraint) =>
        constraint.source_type === 'NOTICE' ||
        keyConstraint.includes(constraint),
    );
  };

  const displayedPivoted = pivoted
    .filter((entry) =>
      Object.values(entry.byDate).some(
        (constraintsForDate: any) =>
          displayedConstraints(constraintsForDate).length > 0,
      ),
    )
    .sort((left, right) => {
      if (sortMode === 'path') return 0;

      const leftConstraints = Object.values(left.byDate).flatMap(
        (constraintsForDate: any) => displayedConstraints(constraintsForDate),
      );
      const rightConstraints = Object.values(right.byDate).flatMap(
        (constraintsForDate: any) => displayedConstraints(constraintsForDate),
      );
      if (sortMode === 'impact') {
        return compareConstraints(
          [...leftConstraints].sort(compareConstraints)[0] ?? {},
          [...rightConstraints].sort(compareConstraints)[0] ?? {},
        );
      }

      const leftRank = Math.max(
        ...leftConstraints.map((constraint: any) =>
          Number(constraint.constraint_priority_rank ?? -Infinity),
        ),
      );
      const rightRank = Math.max(
        ...rightConstraints.map((constraint: any) =>
          Number(constraint.constraint_priority_rank ?? -Infinity),
        ),
      );
      return rightRank - leftRank;
    });

  const severityColor = (severity: number) => {
    if (severity <= 0) return 'transparent';
    if (severity == 1) return '#fff4b3';
    if (severity == 2) return '#ff7272ff';
    if (severity == 3) return '#ff0000ff';
    return 'transparent';
  };

  const constraintHeaderStyle: React.CSSProperties | undefined =
    stickyHeaderTop === undefined
      ? undefined
      : {
          position: 'sticky',
          top: stickyHeaderTop,
          zIndex: 2,
        };

  const constraintGridStyle: React.CSSProperties = {
    gridTemplateColumns: `${
      showSegment ? 'minmax(150px, 1fr) ' : ''
    }minmax(180px, 2fr) minmax(90px, 0.75fr) repeat(${
      dates.length
    }, minmax(140px, 1fr))`,
  };

  const renderConstraintDateCell = (row: any, date: string) => {
    const constraintsForDate = displayedConstraints(row.byDate[date] ?? []);
    if (constraintsForDate.length === 0) return '';
    return (
      <div className="d-grid gap-1">
        {constraintsForDate.map((c: any, index: number) => {
          const constraintKey = String(c.constraint_key ?? '');
          const pathRank = Number(c.path_rank);
          const bg = severityColor(c.severity ?? 0);
          const tooltipId =
            `${tooltipIdPrefix}-${row._key}-${date}-${index}`.replace(
              /[^a-zA-Z0-9_-]/g,
              '',
            );
          return (
            <React.Fragment key={`${constraintKey}-${index}`}>
              <div
                id={tooltipId}
                className="p-2 text-center"
                style={{
                  backgroundColor: bg,
                  color: getContrastColor(bg),
                  cursor: 'help',
                }}
              >
                <span className="fw-bold">
                  {c.resolved_constraint_priority}
                </span>{' '}
                ({numberFormatter(c.max_volume, 0)})<br />
                {c.source_type === 'NOTICE' ? (
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={() => onSelectNotice?.(Number(c.source_id))}
                  >
                    View Notice
                  </button>
                ) : null}
                {showAvoidConstraints &&
                  constraintKey &&
                  Number.isFinite(pathRank) && (
                    <div className="d-inline-flex gap-1">
                      <button
                        type="button"
                        className={`btn btn-sm ${
                          isAvoided(constraintKey, pathRank, 'up_to')
                            ? 'btn-warning'
                            : 'btn-outline-secondary'
                        }`}
                        title={
                          isAvoided(constraintKey, pathRank, 'up_to')
                            ? `Avoiding at rank ≤ ${pathRank} (click to remove)`
                            : `Avoid at rank ≤ ${pathRank}`
                        }
                        onClick={() =>
                          onToggle(
                            constraintKey,
                            pathRank,
                            c.description,
                            'up_to',
                          )
                        }
                      >
                        <CircleMinus />
                        <span className="ms-1">
                          {isAvoided(constraintKey, pathRank, 'up_to')
                            ? 'Avoiding ≤'
                            : 'Avoid ≤'}
                        </span>
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${
                          isAvoided(constraintKey, pathRank, 'all')
                            ? 'btn-danger'
                            : 'btn-outline-danger'
                        }`}
                        title={
                          isAvoided(constraintKey, pathRank, 'all')
                            ? 'Avoiding all ranks (click to remove)'
                            : 'Avoid all ranks'
                        }
                        onClick={() =>
                          onToggle(
                            constraintKey,
                            pathRank,
                            c.description,
                            'all',
                          )
                        }
                      >
                        <span>All</span>
                      </button>
                    </div>
                  )}
              </div>
              <UncontrolledTooltip placement="top" target={tooltipId}>
                {c.description}
              </UncontrolledTooltip>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  if (displayedPivoted.length === 0) {
    return (
      <div>
        {constraints.length}{' '}
        {constraints.length === 1 ? 'constraint is' : 'constraints are'} hidden
        by the {displayMode === 'key' ? 'Key' : 'Worst'} filter.{' '}
        <button
          type="button"
          className="btn btn-sm btn-link p-0 align-baseline"
          onClick={onShowAllConstraints}
        >
          Show all
        </button>
      </div>
    );
  }

  return (
    <div className="constraint-grid" style={constraintGridStyle}>
      <div
        className="constraint-grid-header bg-body fw-semibold"
        style={{ ...constraintGridStyle, ...constraintHeaderStyle }}
      >
        {showSegment && <div className="bg-body">Contract</div>}
        <div className="bg-body">Component</div>
        <div className="bg-body">Path Priority</div>
        {dates.map((date) => (
          <div className="bg-body" key={date}>
            Constraints - {date}
          </div>
        ))}
      </div>
      {displayedPivoted.map((row) => (
        <React.Fragment key={row._key}>
          {showSegment && (
            <div className="constraint-grid-cell">{row.segment_label}</div>
          )}
          <div className="constraint-grid-cell">{row.description}</div>
          <div className="constraint-grid-cell">
            {row.resolved_path_priority}
          </div>
          {dates.map((date) => (
            <div className="constraint-grid-cell" key={date}>
              {renderConstraintDateCell(row, date)}
            </div>
          ))}
        </React.Fragment>
      ))}
    </div>
  );
}

export type PathTableProps = {
  pathData: PathReport[] | undefined;
  initialSortKey?: string;
  initialSortDir?: 'asc' | 'desc';
  sortable?: boolean;
  expandAll?: boolean;
  showAvoidConstraints?: boolean;
};

export type PathTableRef = {
  focusPath: (path: PathReport) => void;
  filterPaths: (path: PathReport) => void;
  showPath: (path: PathReport) => void;
};

/**
 * Reusable PathTable component.
 */
export const PathTable = forwardRef<PathTableRef, PathTableProps>(
  (
    {
      pathData,
      expandAll,
      sortable = false,
      initialSortKey = 'profit',
      initialSortDir = 'desc',
      showAvoidConstraints = false,
    },
    ref,
  ) => {
    const analytics = useAnalytics();
    const dispatch = useDispatch();
    const setTspFilter = useTspFilter();
    const [theme] = useTheme();
    const fitMap = useFitMap();
    const selectionContext = React.useContext(PathReportSelectionContext);
    const { map, registerClickListener, unregisterClickListener } =
      useMapContext();
    const points = useSelector(selectAllPoints);
    const nodes = useSelector(selectAllNodes);
    const tsps = useSelector(selectSortedTsps);
    const comma = (n: any) => {
      const x = Number(n);
      return Number.isFinite(x) ? x.toLocaleString('en-US') : n ?? '';
    };
    const [focusedPath, setFocusedPath] = useState<PathReport | null>(null);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [allPathsExpanded, setAllPathsExpanded] = useState(false);
    const [sortKey, setSortKey] = useState<string>(initialSortKey);
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>(initialSortDir);
    const [selectedNoticeId, setSelectedNoticeId] = useState<number | null>(
      null,
    );
    const [constraintDisplayMode, setConstraintDisplayMode] =
      useLocalStorage<ConstraintDisplayMode>(
        'path_table_constraint_display_mode',
        'key',
      );
    const [showAllConstraintNotices, setShowAllConstraintNotices] =
      useLocalStorage('path_table_show_all_constraint_notices', true);
    const [flowHistoryPath, setFlowHistoryPath] = useState<PathReport | null>(
      null,
    );
    const [flowHistoryEndGasDate, setFlowHistoryEndGasDate] = useState('');
    const [flowHistoryStartGasDate, setFlowHistoryStartGasDate] = useState('');
    const [flowHistoryLimit, setFlowHistoryLimit] = useState(30);
    const [isFlowHistoryDateRangeOpen, setIsFlowHistoryDateRangeOpen] =
      useState(false);
    const [flowHistoryCycleColumnMode, setFlowHistoryCycleColumnMode] =
      useLocalStorage<'all_cycles' | 'any_record' | 'flow_record'>(
        'path_flow_history_cycle_column_mode',
        'all_cycles',
      );
    const [flowHistoryData, setFlowHistoryData] = useState<PathFlows | null>(
      null,
    );
    const [visiblePaths, setVisiblePaths] = useState<Set<PathReport>>(
      new Set(),
    );
    const [editedPathOverrides, setEditedPathOverrides] = useState<
      Record<string, PathReport>
    >({});
    const [editedPathSpecs, setEditedPathSpecs] = useState<
      Record<string, PathSpec>
    >({});
    const [pendingEditFocusKey, setPendingEditFocusKey] = useState<
      string | null
    >(null);
    const [contractSelection, setContractSelection] = useState<{
      key: string;
      path: PathReport;
      segmentIndex: number;
      currentContractId: number | null;
    } | null>(null);
    const [priceSelection, setPriceSelection] = useState<{
      key: string;
      path: PathReport;
      endpoint: 'buy' | 'sell';
      pathSpec: PathSpec;
    } | null>(null);

    const stickyHeaderRef = useRef<HTMLDivElement | null>(null);
    const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
    const tableRef = useRef<HTMLDivElement | null>(null);
    const rowRefs = useRef(new Map<string, HTMLDivElement>());

    useEffect(() => {
      const stickyHeader = stickyHeaderRef.current;
      if (!stickyHeader) return;

      const updateStickyHeaderHeight = () =>
        setStickyHeaderHeight(stickyHeader.offsetHeight);
      const resizeObserver = new ResizeObserver(updateStickyHeaderHeight);

      updateStickyHeaderHeight();
      resizeObserver.observe(stickyHeader);
      return () => resizeObserver.disconnect();
    }, []);

    // Fetch watchlists to compute watchlist_path_name client-side
    const { data: watchlists = [] } = useGetWatchlistsQuery({
      view_all: false,
      with_paths: true,
    });

    const pathDataWithContractOverrides = useMemo(
      () =>
        pathData?.map((path) => {
          const key = pathEditSelectionKey(path);
          const resolvedPath = editedPathOverrides[key] ?? path;
          return {
            ...resolvedPath,
            path_spec: editedPathSpecs[key] ?? pathSpecFromReport(resolvedPath),
          };
        }),
      [pathData, editedPathOverrides, editedPathSpecs],
    );

    // Compute watchlist display name for each path client-side using shared hook
    const enrichedPathData = useEnrichedPaths(
      pathDataWithContractOverrides,
      watchlists,
    );

    const [
      getSegmentContractOptions,
      {
        data: segmentContractOptions,
        isLoading: isLoadingContractOptions,
        isError: hasContractOptionsError,
        reset: resetContractOptions,
      },
    ] = useGetSegmentContractOptionsMutation();
    const [
      getPriceDefinitionOptions,
      {
        data: priceDefinitionOptions,
        isLoading: isLoadingPriceDefinitionOptions,
        isError: hasPriceDefinitionOptionsError,
        reset: resetPriceDefinitionOptions,
      },
    ] = useGetPriceDefinitionOptionsMutation();
    const [
      resolvePathSpec,
      {
        isLoading: isResolvingPathSpec,
        isError: hasResolvePathSpecError,
        reset: resetResolvePathSpec,
      },
    ] = useResolvePathSpecMutation();

    const openContractSelection = async (
      path: PathReport,
      segmentIndex: number,
      segment: ResolvedContractSegmentSummary,
    ) => {
      const key = pathEditSelectionKey(path);
      const pathSpec = editedPathSpecs[key] ?? pathSpecFromReport(path);
      resetResolvePathSpec();
      setContractSelection({
        key,
        path,
        segmentIndex,
        currentContractId: segment.contract_id,
      });
      try {
        await getSegmentContractOptions({
          segment_index: segmentIndex,
          scenario_id:
            path.scenario_id ?? selectionContext?.selected.scenario_id,
          gas_date: path.gas_date ?? selectionContext?.selected.gas_date,
          path_spec: pathSpec,
          buy_asset_description: path.buy_asset_description,
          sell_asset_description: path.sell_asset_description,
        }).unwrap();
      } catch (_error) {
        // The modal owns the request error state.
      }
    };

    const applyContractOption = async (
      option: SegmentContractOption,
      useBest: boolean,
    ) => {
      if (!contractSelection || !option.preview) return;

      const pathSpec = useBest
        ? {
            ...option.path_spec,
            segments: option.path_spec.segments.map((segmentSpec, index) =>
              index === contractSelection.segmentIndex
                ? { ...segmentSpec, contract_spec: { type: 'any' as const } }
                : segmentSpec,
            ),
          }
        : option.path_spec;
      try {
        const response = await resolvePathSpec({
          scenario_id:
            contractSelection.path.scenario_id ??
            selectionContext?.selected.scenario_id,
          gas_date:
            contractSelection.path.gas_date ??
            selectionContext?.selected.gas_date,
          path_spec: pathSpec,
          buy_asset_description: contractSelection.path.buy_asset_description,
          sell_asset_description: contractSelection.path.sell_asset_description,
        }).unwrap();
        setEditedPathSpecs((current) => ({
          ...current,
          [contractSelection.key]: response.path_spec,
        }));
        setEditedPathOverrides((current) => ({
          ...current,
          [contractSelection.key]: {
            ...contractSelection.path,
            ...response.path,
            report_row_id: contractSelection.path.report_row_id,
            path_spec: response.path_spec,
          },
        }));
        setExpanded((current) => new Set(current).add(contractSelection.key));
        setPendingEditFocusKey(contractSelection.key);
        resetContractOptions();
        resetResolvePathSpec();
        setContractSelection(null);
      } catch (_error) {
        // The modal owns the request error state.
      }
    };

    const openPriceSelection = async (
      path: PathReport,
      endpoint: 'buy' | 'sell',
    ) => {
      const key = pathEditSelectionKey(path);
      const pathSpec = editedPathSpecs[key] ?? pathSpecFromReport(path);
      resetResolvePathSpec();
      setPriceSelection({ key, path, endpoint, pathSpec });
      try {
        await getPriceDefinitionOptions({
          scenario_id:
            path.scenario_id ?? selectionContext?.selected.scenario_id,
          gas_date: path.gas_date ?? selectionContext?.selected.gas_date,
        }).unwrap();
      } catch (_error) {
        // The modal owns the request error state.
      }
    };

    const applyPriceDefinition = async (definition: PriceDefinitionSpec) => {
      if (!priceSelection) return;

      const definitionField =
        priceSelection.endpoint === 'buy'
          ? 'buy_price_definition'
          : 'sell_price_definition';
      const nextPathSpec: PathSpec = {
        ...priceSelection.pathSpec,
        [definitionField]: definition,
      };

      try {
        const response = await resolvePathSpec({
          scenario_id:
            priceSelection.path.scenario_id ??
            selectionContext?.selected.scenario_id,
          gas_date:
            priceSelection.path.gas_date ?? selectionContext?.selected.gas_date,
          path_spec: nextPathSpec,
          buy_asset_description: priceSelection.path.buy_asset_description,
          sell_asset_description: priceSelection.path.sell_asset_description,
        }).unwrap();
        setEditedPathSpecs((current) => ({
          ...current,
          [priceSelection.key]: response.path_spec,
        }));
        setEditedPathOverrides((current) => ({
          ...current,
          [priceSelection.key]: {
            ...priceSelection.path,
            ...response.path,
            report_row_id: priceSelection.path.report_row_id,
            path_spec: response.path_spec,
          },
        }));
        setExpanded((current) => new Set(current).add(priceSelection.key));
        setPendingEditFocusKey(priceSelection.key);
        resetPriceDefinitionOptions();
        resetResolvePathSpec();
        setPriceSelection(null);
      } catch (_error) {
        // The modal owns the request error state.
      }
    };

    const [
      getPathFlows,
      { isLoading: isLoadingFlows, error: flowsError, reset },
    ] = useGetPathFlowsMutation();

    const mergePathFlows = (
      existingData: PathFlows | null,
      nextData: PathFlows,
    ): PathFlows => {
      if (!existingData) return nextData;

      const mergeRow = (existingRow: any, nextRow: any) => ({
        ...existingRow,
        ...nextRow,
        flows: [...(existingRow.flows || []), ...(nextRow.flows || [])],
        notice_slice: {
          ...existingRow.notice_slice,
          ...nextRow.notice_slice,
          records_with_date_cycle: [
            ...(existingRow.notice_slice?.records_with_date_cycle || []),
            ...(nextRow.notice_slice?.records_with_date_cycle || []),
          ],
        },
      });
      const evidenceKey = (row: any) =>
        [
          row.source_kind,
          row.child_kind,
          row.label,
          JSON.stringify(row.key),
        ].join('|');
      const existingGroups = new Map(
        existingData.groups.map((group) => [group.group_id, group]),
      );

      return {
        groups: nextData.groups.map((nextGroup) => {
          const existingGroup = existingGroups.get(nextGroup.group_id);
          if (!existingGroup) return nextGroup;

          const existingEvidence = new Map(
            existingGroup.evidence_rows.map((row) => [evidenceKey(row), row]),
          );
          return {
            ...existingGroup,
            ...nextGroup,
            parent_row: mergeRow(
              existingGroup.parent_row,
              nextGroup.parent_row,
            ),
            evidence_rows: nextGroup.evidence_rows.map((nextRow) =>
              mergeRow(
                existingEvidence.get(evidenceKey(nextRow)) || {},
                nextRow,
              ),
            ),
          };
        }),
      };
    };

    const loadPathFlows = async (
      path: PathReport,
      endGasDate: string,
      limit: number,
      append = false,
    ) => {
      try {
        const data = await getPathFlows({
          path_elements: path.path_elements,
          limit,
          offset: endGasDate,
          filter_by_direction: true,
          include_empty: false,
        }).unwrap();
        setFlowHistoryData((previousData) =>
          append ? mergePathFlows(previousData, data) : data,
        );
        return true;
      } catch {
        // The mutation state supplies the existing error presentation.
        return false;
      }
    };

    const openPathFlows = (path: PathReport) => {
      const endGasDate = path.gas_date;
      setFlowHistoryPath(path);
      setFlowHistoryEndGasDate(endGasDate);
      setFlowHistoryStartGasDate('');
      setFlowHistoryLimit(30);
      setIsFlowHistoryDateRangeOpen(false);
      setFlowHistoryData(null);
      loadPathFlows(path, endGasDate, 30);
    };

    const loadOlderPathFlows = () => {
      if (!flowHistoryPath || flowHistoryStartGasDate) return;

      const previousPageEnd = new Date(`${flowHistoryEndGasDate}T00:00:00Z`);
      previousPageEnd.setUTCDate(
        previousPageEnd.getUTCDate() - flowHistoryLimit - 1,
      );
      const previousPageEndGasDate = previousPageEnd.toISOString().slice(0, 10);
      const pageLimit = 30;
      void loadPathFlows(
        flowHistoryPath,
        previousPageEndGasDate,
        pageLimit,
        true,
      ).then((didLoad) => {
        if (didLoad) {
          setFlowHistoryLimit((previousLimit) => previousLimit + pageLimit + 1);
        }
      });
    };

    const getFlowHistoryRangeLimit = (
      startGasDate: string,
      endGasDate: string,
    ) => {
      const start = new Date(`${startGasDate}T00:00:00Z`);
      const end = new Date(`${endGasDate}T00:00:00Z`);
      const millisecondsPerDay = 24 * 60 * 60 * 1000;
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return null;
      }

      const limit = Math.round(
        (end.getTime() - start.getTime()) / millisecondsPerDay,
      );
      return limit >= 0 ? limit : null;
    };

    const getFlowHistoryRangeStart = (endGasDate: string, limit: number) => {
      const end = new Date(`${endGasDate}T00:00:00Z`);
      if (Number.isNaN(end.getTime())) return '';

      end.setUTCDate(end.getUTCDate() - limit);
      return end.toISOString().slice(0, 10);
    };

    const asMapPath = useCallback(
      (path: PathReport): Path => ({
        nodes: tuplesToList(path.node_tuples),
        color: generateColor(
          path.original_path_fingerprint,
          80,
          theme === 'light' ? 50 : 60,
        ),
        getProperties: () => {
          return {
            key: `path-${path.original_path_fingerprint}`,
            id: path.original_path_fingerprint,
          };
        },
      }),
      [theme],
    );

    const showSinglePath = useCallback((path: PathReport) => {
      setVisiblePaths(new Set([path]));
    }, []);

    useEffect(() => {
      setVisiblePaths(new Set(enrichedPathData));
    }, [enrichedPathData, asMapPath, mapActions, dispatch]);

    useEffect(() => {
      dispatch(mapActions.setPaths(Array.from(visiblePaths).map(asMapPath)));
    }, [visiblePaths, asMapPath, dispatch, mapActions]);

    useEffect(() => {
      return () => {
        dispatch(mapActions.setPaths([]));
      };
    }, []);

    const displayPath = (path: PathReport) => {
      dispatch(mapActions.setPaths([asMapPath(path)]));
    };

    const hidePath = () => {
      dispatch(mapActions.setPaths(Array.from(visiblePaths).map(asMapPath)));
    };

    const hasContractUsageAvailability = (enrichedPathData ?? []).some(
      (path) => path.contract_usage_availability != null,
    );

    const columns: DynamicTableProps<PathReport>['columns'] = useMemo(
      () => [
        {
          key: 'visible',
          header: (
            <span className="d-flex justify-content-center">
              {visiblePaths.size > 0 ? (
                <button
                  type="button"
                  className="btn btn-link border-0 p-0 d-inline-flex align-items-center justify-content-center lh-1 text-decoration-none text-secondary"
                  onClick={() => setVisiblePaths(new Set())}
                  title="Hide all paths"
                  aria-label="Hide all paths"
                >
                  <Eye />
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-link border-0 p-0 d-inline-flex align-items-center justify-content-center lh-1 text-decoration-none"
                  onClick={() => setVisiblePaths(new Set(enrichedPathData))}
                  title="Show all paths"
                  aria-label="Show all paths"
                >
                  <EyeOff />
                </button>
              )}
            </span>
          ),
          width: '36px',
          render: (row) => {
            return (
              <div className="d-flex justify-content-center">
                {visiblePaths.has(row) ? (
                  <button
                    type="button"
                    className="btn btn-link border-0 p-0 d-inline-flex align-items-center justify-content-center lh-1 text-decoration-none text-secondary"
                    onContextMenu={(evt) => {
                      showSinglePath(row);
                      evt.preventDefault();
                      evt.stopPropagation();
                    }}
                    onClick={() =>
                      setVisiblePaths((prev) => {
                        const next = new Set(prev);
                        next.delete(row);
                        return next;
                      })
                    }
                    title="Hide path"
                    aria-label="Hide path"
                  >
                    <Eye />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-link border-0 p-0 d-inline-flex align-items-center justify-content-center lh-1 text-decoration-none text-secondary"
                    onContextMenu={(evt) => {
                      showSinglePath(row);
                      evt.preventDefault();
                      evt.stopPropagation();
                    }}
                    onClick={() =>
                      setVisiblePaths((prev) => {
                        const next = new Set(prev);
                        next.add(row);
                        return new Set(next);
                      })
                    }
                    title="Show path"
                    aria-label="Show path"
                  >
                    <EyeOff size={26} />
                  </button>
                )}
              </div>
            );
          },
        },
        {
          key: '__watchlist',
          header: '',
          width: '36px',
          render: (row: PathReport) => {
            return <AddToWatchlistButton path={row} />;
          },
        },
        {
          key: '__share',
          header: '',
          width: '36px',
          render: (row: PathReport) => {
            return <SharePathButton path={row} />;
          },
        },
        {
          key: '__filter',
          header: '',
          width: '36px',
          render: (row: PathReport) => {
            return (
              <button
                type="button"
                className="btn btn-link border-0 p-0 d-inline-flex align-items-center justify-content-center lh-1 text-decoration-none text-secondary"
                onClick={() => focusPath(row)}
                title="Focus path"
                aria-label="Focus path"
              >
                <Filter />
              </button>
            );
          },
        },
        {
          key: '__warnings',
          header: '',
          width: '36px',
          render: (row: PathReport) => {
            const issues: ResolvedContractSegmentSummary[] =
              row?.resolved_contract_segment_summaries?.filter(
                (summary) => summary?.resolution_issues?.length > 0,
              ) ?? [];
            const rowKey = pathEditSelectionKey(row).replace(
              /[^a-zA-Z0-9]/g,
              '',
            );

            const issuesText: PartialRecord<string, string> = {
              no_resolved_contracts: 'No resolved contracts for this segment',
              no_best_contract:
                'No best contract could be identified for this segment',
              segment_not_resolved: 'Segment could not be resolved',
            };
            return (
              issues.length > 0 && (
                <>
                  <TriangleAlert
                    id={`path-${rowKey}-warning`}
                    color="var(--bs-warning)"
                  />
                  <UncontrolledTooltip target={`path-${rowKey}-warning`}>
                    {issues.map((summary) => {
                      return (
                        <div
                          key={`${summary.r_point_name}-${summary.d_point_name}`}
                        >
                          <strong>
                            From {summary.r_point_name} to{' '}
                            {summary.d_point_name}:
                          </strong>
                          {summary.resolution_issues.map((issue, idx) => (
                            <div key={idx}>
                              {issuesText[issue] ?? `Unknown issue: ${issue}`}
                            </div>
                          ))}
                          <br />
                        </div>
                      );
                    })}
                  </UncontrolledTooltip>
                </>
              )
            );
          },
        },
        {
          key: 'buy_pool',
          header: 'Buy Pool',
          width: '1fr',
          render: (row) => (
            <button
              type="button"
              className="path-edit-control path-edit-control-pool"
              onClick={(event) => {
                event.stopPropagation();
                openPriceSelection(row, 'buy');
              }}
              title="Change buy price"
              aria-label={`Change buy price: ${formatPoolWithAdder(
                row.buy_pool,
                row.buy_adder,
              )}`}
            >
              <span className="path-edit-control-label path-edit-control-label-wrap">
                {formatPoolWithAdder(row.buy_pool, row.buy_adder)}
              </span>
              <Pencil size={12} aria-hidden="true" />
            </button>
          ),
        },
        {
          key: 'sell_pool',
          header: 'Sell Pool',
          width: '1fr',
          render: (row) => (
            <button
              type="button"
              className="path-edit-control path-edit-control-pool"
              onClick={(event) => {
                event.stopPropagation();
                openPriceSelection(row, 'sell');
              }}
              title="Change sell price"
              aria-label={`Change sell price: ${formatPoolWithAdder(
                row.sell_pool,
                row.sell_adder,
              )}`}
            >
              <span className="path-edit-control-label path-edit-control-label-wrap">
                {formatPoolWithAdder(row.sell_pool, row.sell_adder)}
              </span>
              <Pencil size={12} aria-hidden="true" />
            </button>
          ),
        },
        {
          key: 'r_point_id',
          header: 'Receipt',
          width: 'minmax(100px, 0.9fr)',
          render: (row) => (
            <PointBadge
              point={points[row.r_point_id]}
              theme={theme}
              showTspName
            />
          ),
        },
        {
          key: 'd_point_id',
          header: 'Delivery',
          width: 'minmax(100px, 0.9fr)',
          render: (row) => (
            <PointBadge
              point={points[row.d_point_id]}
              theme={theme}
              showTspName
            />
          ),
        },
        {
          key: 'route_list',
          header: 'Route',
          width: 'minmax(360px, 5fr)',
          render: (row) => {
            const list = row.route_list ?? [];
            const routePieces = [];
            for (let index = 0; index < list.length; index += 2) {
              routePieces.push({
                pipeline: list[index],
                interconnect: list[index + 1],
              });
            }

            return (
              <div className="route-list">
                {routePieces.map((piece, index) => (
                  <div className="route-piece" key={index}>
                    <span className="route-pipeline" title={piece.pipeline}>
                      {piece.pipeline}
                    </span>
                    {piece.interconnect && (
                      <>
                        <span className="route-separator">&gt;</span>
                        <span
                          className="route-interconnect"
                          title={piece.interconnect}
                        >
                          {piece.interconnect}
                        </span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            );
          },
        },

        {
          key: 'buy_price',
          header: 'Buy Price',
          width: '100px',
          render: (r) => (r.buy_price ? `$${r.buy_price}` : 'N/A'),
        },
        {
          key: 'sell_price',
          header: 'Sell Price',
          width: '100px',
          render: (r) => (r.sell_price ? `$${r.sell_price}` : 'N/A'),
        },
        // { key: "contract_list", header: "Contract List", render: ListRenderer("contract_list") },
        {
          key: 'max_volume',
          header: 'Max Volume',
          width: '1fr',
          render: (r) => comma(r.max_volume),
        },
        ...(hasContractUsageAvailability
          ? [
              {
                key: 'contract_usage_availability',
                header: 'Contract Availability',
                width: 'minmax(155px, 1.2fr)',
                render: (row: PathReport, rowIndex: number) => {
                  if (!row.contract_usage_availability) return '—';
                  const display = describeContractUsageAvailability(
                    row.contract_usage_availability,
                  );
                  const limiterLabels = getContractUsageLimiterLabels(
                    row.contract_usage_availability.limiting_contract_ids,
                    row.resolved_contract_segment_summaries ?? [],
                  );
                  const showLimiterTooltip =
                    display.status !== 'unaffected' && limiterLabels.length > 0;
                  const tooltipId = `contract-availability-${rowIndex}-${row.original_path_fingerprint.replace(
                    /[^a-zA-Z0-9]/g,
                    '',
                  )}`;
                  const className =
                    display.status === 'unavailable'
                      ? 'text-danger fw-semibold'
                      : display.status === 'partial'
                      ? 'text-warning fw-semibold'
                      : '';
                  return (
                    <>
                      <span
                        className={className}
                        id={showLimiterTooltip ? tooltipId : undefined}
                        title={
                          showLimiterTooltip
                            ? undefined
                            : 'Available after Contract Usage'
                        }
                      >
                        {display.label}
                      </span>
                      {showLimiterTooltip && (
                        <UncontrolledTooltip placement="top" target={tooltipId}>
                          <div className="fw-semibold">
                            Limited by Contract Usage
                          </div>
                          {limiterLabels.map((label, index) => (
                            <div key={`${index}-${label}`}>{label}</div>
                          ))}
                        </UncontrolledTooltip>
                      )}
                    </>
                  );
                },
              },
            ]
          : []),
        {
          key: 'profit_per_dth',
          header: '$/dth',
          width: '1fr',
          render: (r) => (r.profit_per_dth ? `$${r.profit_per_dth}` : 'N/A'),
        },
        {
          key: 'profit',
          header: 'Profit',
          width: '1fr',
          render: (r) => (r.profit ? `$${comma(Math.round(r.profit))}` : 'N/A'),
        },
        {
          key: 'transport_cost',
          header: 'Transport Cost',
          width: '1fr',
          render: (r) => (r.transport_cost ? `$${r.transport_cost}` : 'N/A'),
        },
        // could re-enable this if a flag is selected, it takes up a lot of realestate but is useful
        // {
        //     key: "all_constraints",
        //     header: "Worst Constraints",
        //     render: (r: any) => (
        //         <PivotConstraintTable constraints={r.all_constraints.filter((c: any) => Number(c.severity) === 2)} />
        //     ),
        // },
        {
          key: 'components_along_path',
          width: '0.7fr',
          header: 'Flow History',
          render: (r) => (
            <button
              className="btn btn-sm btn-outline-info"
              onClick={() => {
                if (!r.path_elements || r.path_elements.length === 0) {
                  console.error('No path_elements available for this path');
                  return;
                }

                openPathFlows(r);
              }}
              disabled={
                isLoadingFlows ||
                !r.path_elements ||
                r.path_elements.length === 0
              }
            >
              {isLoadingFlows ? 'Loading...' : 'Flows'}
            </button>
          ),
        },
        {
          key: 'details',
          width: '0.7fr',
          header: 'Details',
          render: (row) => {
            const key = pathEditSelectionKey(row);
            const isExpanded =
              expandAll === true || allPathsExpanded || expanded.has(key);
            return (
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => {
                  if (allPathsExpanded) {
                    setAllPathsExpanded(false);
                    setExpanded(
                      new Set(
                        (enrichedPathData ?? [])
                          .map(pathEditSelectionKey)
                          .filter((pathKey) => pathKey !== key),
                      ),
                    );
                    return;
                  }
                  setExpanded((prev) => {
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  });
                }}
              >
                {isExpanded ? 'Hide' : 'Details'}
              </button>
            );
          },
        },
      ],
      [
        expandAll,
        allPathsExpanded,
        expanded,
        enrichedPathData,
        visiblePaths,
        pathEditSelectionKey,
      ],
    );

    // Build grid template from column definitions: fixed widths respected; others fill
    const gridTemplateColumns = useMemo(
      () => columns.map((c) => c.width ?? 'minmax(0, 1fr)').join(' '),
      [columns],
    );

    // Sorting: derive a sorted copy of enrichedPathData when enabled
    const sortedPathData: PathReport[] = useMemo(() => {
      if (!sortable || !sortKey) return enrichedPathData ?? [];
      const data = Array.from(enrichedPathData ?? []);
      const getVal = (row: any) => row?.[sortKey];
      const cmp = (a: any, b: any) => {
        const va = getVal(a);
        const vb = getVal(b);
        const na = Number(va);
        const nb = Number(vb);
        const bothNums = Number.isFinite(na) && Number.isFinite(nb);
        let res = 0;
        if (bothNums) {
          res = na - nb;
        } else {
          const sa = String(va ?? '');
          const sb = String(vb ?? '');
          res = sa.localeCompare(sb);
        }
        return sortDir === 'asc' ? res : -res;
      };
      data.sort(cmp);
      return data;
    }, [enrichedPathData, sortable, sortKey, sortDir]);

    useEffect(() => {
      if (!pendingEditFocusKey) return;

      const row = rowRefs.current.get(pendingEditFocusKey);
      if (!row) return;

      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setPendingEditFocusKey(null);
    }, [pendingEditFocusKey, sortedPathData]);

    const scrollToRow = useCallback(
      (index: number) => {
        // scroll into child index + 1 of tableRef (to account for header row)
        const table = tableRef.current;
        if (!table) return;
        const row = table.children[index] as HTMLElement | undefined;
        if (row) {
          row.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          });
        }
      },
      [tableRef],
    );

    const showPath = useCallback(
      (path: PathReport) => {
        setFocusedPath(path);
      },
      [fitMap, showSinglePath, sortedPathData, setTspFilter, nodes, points],
    );

    useEffect(() => {
      if (!focusedPath) return;
      const pathNodes = Array.from(
        new Set(
          focusedPath.node_tuples.flatMap(([r, d]) => {
            const rNode = nodes[r];
            const dNode = nodes[d];
            return [rNode, dNode];
          }),
        ),
      );
      const coords = pathNodes.filter(Boolean).map((node) => {
        return [node?.lat, node?.lng] as [string, string];
      });
      filterTsps(focusedPath);
      fitMap(coords);
      showSinglePath(focusedPath);
      scrollToRow(sortedPathData.indexOf(focusedPath));
    }, [
      focusedPath,
      nodes,
      points,
      fitMap,
      showSinglePath,
      scrollToRow,
      sortedPathData,
    ]);

    const filterTsps = useCallback(
      (path: PathReport) => {
        const tspIds =
          (path.tsp_list
            .map((tsp_name) => tsps.find((tsp) => tsp.name === tsp_name)?.id)
            .filter(Boolean) as number[]) ?? [];
        setTspFilter(tspIds);
        showSinglePath(path);
      },
      [setTspFilter, showSinglePath, tsps],
    );

    const focusPath = useCallback(
      (path: PathReport) => {
        filterTsps(path);
        showPath(path);
      },
      [filterTsps, showPath],
    );

    useImperativeHandle(
      ref,
      () => ({
        focusPath,
        filterPaths: filterTsps,
        showPath,
      }),
      [focusPath, filterTsps, showPath],
    );

    const clickListener: MapEventListener = useCallback(
      (feature) => {
        if (!feature || !feature?.getProperties()?.key?.startsWith('path-'))
          return;
        const pathId = feature.getProperties()?.id;
        const path = sortedPathData?.find(
          (p) => p.original_path_fingerprint === pathId,
        );
        if (path) {
          analytics.logEvent(AnalyticsEvent.CLICKED_PATH, {
            path: JSON.stringify(path),
          });
          showPath(path);
        }
      },
      [showPath, sortedPathData],
    );

    useEffect(() => {
      registerClickListener(clickListener);
      return () => {
        unregisterClickListener(clickListener);
      };
    }, [clickListener, registerClickListener, unregisterClickListener, map]);

    return (
      <div
        style={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'auto',
        }}
      >
        <div
          ref={stickyHeaderRef}
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 4,
            backgroundColor: 'var(--bs-body-bg)',
            minWidth: 'fit-content',
          }}
        >
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2 p-2 border">
            <span className="small fw-semibold">Details:</span>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => {
                if (allPathsExpanded) {
                  setAllPathsExpanded(false);
                  setExpanded(new Set());
                } else {
                  setAllPathsExpanded(true);
                  setExpanded(new Set());
                }
              }}
            >
              {allPathsExpanded ? 'Collapse All' : 'Expand All'}
            </button>
            <span className="small fw-semibold">Constraints:</span>
            <div
              className="btn-group"
              role="group"
              aria-label="Constraint display"
            >
              {[
                ['key', 'Key'],
                ['worst', 'Worst'],
                ['all', 'All'],
                ['none', 'None'],
              ].map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  className={`btn btn-sm ${
                    constraintDisplayMode === mode
                      ? 'btn-primary'
                      : 'btn-outline-primary'
                  }`}
                  onClick={() =>
                    setConstraintDisplayMode(mode as ConstraintDisplayMode)
                  }
                >
                  {label}
                </button>
              ))}
            </div>
            <label className="form-check form-check-inline mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                checked={showAllConstraintNotices}
                onChange={(event) =>
                  setShowAllConstraintNotices(event.target.checked)
                }
              />
              <span className="form-check-label">
                Show all notice constraints
              </span>
            </label>
          </div>
          <div
            className="border-bottom fw-bold small text-muted px-2 py-2"
            style={{
              display: 'grid',
              gridTemplateColumns: gridTemplateColumns,
              backgroundColor: 'var(--bs-body-bg)',
            }}
          >
            {columns.map((col) => {
              const sortableKeys = new Set([
                'profit',
                'profit_per_dth',
                'max_volume',
                'buy_price',
                'sell_price',
              ]);
              const isSortableCol = sortable && sortableKeys.has(col.key);
              const isActive = isSortableCol && sortKey === col.key;
              const indicator = isActive
                ? sortDir === 'asc'
                  ? ' ▲'
                  : ' ▼'
                : '';
              const label = typeof col.header === 'string' ? col.header : null;
              return (
                <div key={col.key}>
                  {isSortableCol && label ? (
                    <button
                      className="btn btn-link p-0 m-0 text-decoration-none"
                      onClick={() => {
                        setSortKey(col.key);
                        setSortDir((prev) =>
                          isActive ? (prev === 'asc' ? 'desc' : 'asc') : 'desc',
                        );
                      }}
                      title={`Sort by ${label}`}
                    >
                      {label}
                      <span>{indicator}</span>
                    </button>
                  ) : (
                    col.header
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div
          ref={tableRef}
          className="border rounded"
          /* Allow table to not be clipped out of scrolling area on smaller screens. */
          style={{ position: 'relative', minWidth: 'fit-content' }}
        >
          {/* ROWS */}
          {(sortedPathData ?? enrichedPathData).map(
            (row: PathReport, rowIndex: number) => {
              const key = pathEditSelectionKey(row);
              const isExpanded =
                expandAll === true || allPathsExpanded || expanded.has(key);
              const items = (row.resolved_contract_segment_summaries || [])
                .map((segment, pathSegmentIndex) => ({
                  segment,
                  pathSegmentIndex,
                }))
                .filter(({ segment }) => segment?.tsp_name != null);
              const pathConstraints = items.flatMap(({ segment }) =>
                (segment.evaluated_constraints ?? []).map((constraint) => ({
                  ...constraint,
                  segment_label: `${segment.tsp_name} / ${
                    segment.contract_name ?? 'No contract'
                  }`,
                })),
              );

              return (
                <div
                  key={key}
                  ref={(element) => {
                    if (element) rowRefs.current.set(key, element);
                    else rowRefs.current.delete(key);
                  }}
                  onMouseEnter={() => displayPath(row)}
                  onMouseLeave={hidePath}
                  className="m-2 border rounded"
                >
                  {/* PATH NAME DISPLAY */}
                  {row.watchlist_path_name && (
                    <div
                      className="px-2 py-1 text-muted small fst-italic"
                      style={{
                        backgroundColor: 'var(--bs-tertiary-bg)',
                        fontSize: '0.75rem',
                        borderBottom: '1px solid var(--bs-border-color)',
                      }}
                    >
                      {row.watchlist_path_name}
                    </div>
                  )}

                  {/* ROW CARD */}
                  <div
                    className="px-2 py-2 hover-bg-light"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: gridTemplateColumns,
                    }}
                  >
                    {columns.map((col) => (
                      <div className="px-1" key={col.key}>
                        {col.render
                          ? col.render(row, rowIndex)
                          : (row as any)[col.key]}
                      </div>
                    ))}
                  </div>

                  {/* EXPANDED DETAILS */}
                  {isExpanded && (
                    <div className="border-top border-2 bg-body-secondary p-2">
                      <div className="small fw-semibold text-secondary mb-2">
                        Segment details
                      </div>
                      <table className="table table-sm table-bordered mb-3 path-segment-summary">
                        <thead>
                          <tr>
                            <th>TSP</th>
                            <th>Contract</th>
                            <th>R Point</th>
                            <th>D Point</th>
                            <th>Commodity</th>
                            <th>Fuel %</th>
                            <th>Contract MDQ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map(({ segment, pathSegmentIndex }) => (
                            <tr key={`path-${pathSegmentIndex}-row`}>
                              <td>{segment.tsp_name}</td>
                              <td>
                                {segment.contract_id == null ? (
                                  segment.contract_name
                                ) : (
                                  <button
                                    type="button"
                                    className="path-edit-control"
                                    onClick={() =>
                                      openContractSelection(
                                        row,
                                        pathSegmentIndex,
                                        segment,
                                      )
                                    }
                                    title="Change contract"
                                    aria-label={`Change contract: ${formatContractName(
                                      segment.contract_name,
                                      segment.contract_k_id,
                                    )}`}
                                  >
                                    <span className="path-edit-control-label">
                                      <span className="d-block fw-semibold">
                                        {formatContractName(
                                          segment.contract_name,
                                          segment.contract_k_id,
                                        )}
                                      </span>
                                      {segment.contract_k_id &&
                                        formatContractName(
                                          segment.contract_name,
                                          segment.contract_k_id,
                                        ) !== segment.contract_k_id && (
                                          <span className="d-block small text-muted">
                                            {segment.contract_k_id}
                                          </span>
                                        )}
                                    </span>
                                    <Pencil size={12} aria-hidden="true" />
                                  </button>
                                )}
                              </td>
                              <td>
                                <PointBadge
                                  point={points[segment.segment_r_point_id]}
                                  theme={theme}
                                  showTspName
                                />
                              </td>
                              <td>
                                <PointBadge
                                  point={points[segment.segment_d_point_id]}
                                  theme={theme}
                                  showTspName
                                />
                              </td>
                              <td>{segment.commodity_rate}</td>
                              <td>
                                {numberFormatter(segment.fuel_rate * 100, 2)}
                              </td>
                              <td>
                                {numberFormatter(segment.contractual_volume, 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {constraintDisplayMode !== 'none' && (
                        <>
                          <div className="small fw-semibold text-secondary mb-2 mt-3">
                            Constraints
                          </div>
                          <section className="bg-body">
                            <PivotConstraintTable
                              constraints={pathConstraints}
                              constraintDisplaySettings={{
                                displayMode: constraintDisplayMode,
                                showAllNotices: showAllConstraintNotices,
                                sortMode: 'path',
                              }}
                              showAvoidConstraints={showAvoidConstraints}
                              onSelectNotice={setSelectedNoticeId}
                              onShowAllConstraints={() =>
                                setConstraintDisplayMode('all')
                              }
                              stickyHeaderTop={stickyHeaderHeight}
                              showSegment
                            />
                          </section>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            },
          )}
        </div>

        <SegmentContractPickerModal
          isOpen={Boolean(contractSelection)}
          options={segmentContractOptions?.options ?? []}
          currentContractId={contractSelection?.currentContractId ?? null}
          isLoading={isLoadingContractOptions}
          isApplying={isResolvingPathSpec}
          hasError={hasContractOptionsError || hasResolvePathSpecError}
          onSelect={(option: SegmentContractOption) =>
            applyContractOption(option, false)
          }
          onUseBest={(option: SegmentContractOption) =>
            applyContractOption(option, true)
          }
          onClose={() => {
            resetContractOptions();
            resetResolvePathSpec();
            setContractSelection(null);
          }}
        />

        <PriceDefinitionPickerModal
          isOpen={Boolean(priceSelection)}
          endpoint={priceSelection?.endpoint ?? 'buy'}
          options={priceDefinitionOptions?.options ?? []}
          currentDefinition={
            priceSelection?.endpoint === 'buy'
              ? priceSelection.pathSpec.buy_price_definition
              : priceSelection?.pathSpec.sell_price_definition
          }
          isLoading={isLoadingPriceDefinitionOptions}
          isApplying={isResolvingPathSpec}
          hasError={hasPriceDefinitionOptionsError || hasResolvePathSpecError}
          onApply={applyPriceDefinition}
          onClose={() => {
            resetPriceDefinitionOptions();
            resetResolvePathSpec();
            setPriceSelection(null);
          }}
        />

        <ReusableModal
          style={{ maxWidth: '75vw' }}
          onClose={() => {
            reset(); // Clear the mutation state
            setFlowHistoryPath(null);
            setFlowHistoryData(null);
          }}
          isOpen={Boolean(flowHistoryPath)}
          header={'Path Flows'}
        >
          {flowHistoryPath && (
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <div
                className="btn-group"
                role="group"
                aria-label="History range"
              >
                {[7, 30, 90].map((limit) => (
                  <button
                    key={limit}
                    type="button"
                    className={`btn btn-sm ${
                      flowHistoryLimit === limit && !flowHistoryStartGasDate
                        ? 'btn-primary'
                        : 'btn-outline-primary'
                    }`}
                    onClick={() => {
                      setFlowHistoryLimit(limit);
                      setFlowHistoryStartGasDate('');
                      setIsFlowHistoryDateRangeOpen(false);
                      setFlowHistoryData(null);
                      loadPathFlows(
                        flowHistoryPath,
                        flowHistoryEndGasDate,
                        limit,
                      );
                    }}
                  >
                    {limit} days
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                aria-expanded={isFlowHistoryDateRangeOpen}
                onClick={() => {
                  if (!isFlowHistoryDateRangeOpen) {
                    setFlowHistoryStartGasDate(
                      getFlowHistoryRangeStart(
                        flowHistoryEndGasDate,
                        flowHistoryLimit,
                      ),
                    );
                  }
                  setIsFlowHistoryDateRangeOpen((isOpen) => !isOpen);
                }}
              >
                Date range
              </button>
              <select
                className="form-select form-select-sm"
                aria-label="Flow history columns"
                value={flowHistoryCycleColumnMode}
                onChange={(event) =>
                  setFlowHistoryCycleColumnMode(
                    event.target.value as
                      | 'all_cycles'
                      | 'any_record'
                      | 'flow_record',
                  )
                }
                style={{ width: 'auto' }}
              >
                <option value="all_cycles">All cycles</option>
                <option value="any_record">Any record</option>
                <option value="flow_record">Flow records</option>
              </select>
              {isFlowHistoryDateRangeOpen && (
                <div className="d-flex align-items-end flex-wrap gap-2 w-100">
                  <label className="d-flex flex-column gap-1 small mb-0">
                    <span>Start date</span>
                    <Input
                      type="date"
                      bsSize="sm"
                      value={flowHistoryStartGasDate}
                      max={flowHistoryEndGasDate || undefined}
                      onChange={(event) =>
                        setFlowHistoryStartGasDate(event.target.value)
                      }
                    />
                  </label>
                  <label className="d-flex flex-column gap-1 small mb-0">
                    <span>End date</span>
                    <Input
                      type="date"
                      bsSize="sm"
                      value={flowHistoryEndGasDate}
                      min={flowHistoryStartGasDate || undefined}
                      onChange={(event) =>
                        setFlowHistoryEndGasDate(event.target.value)
                      }
                    />
                  </label>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={
                      getFlowHistoryRangeLimit(
                        flowHistoryStartGasDate,
                        flowHistoryEndGasDate,
                      ) === null
                    }
                    onClick={() => {
                      const limit = getFlowHistoryRangeLimit(
                        flowHistoryStartGasDate,
                        flowHistoryEndGasDate,
                      );
                      if (limit === null) return;

                      setFlowHistoryLimit(limit);
                      setFlowHistoryData(null);
                      loadPathFlows(
                        flowHistoryPath,
                        flowHistoryEndGasDate,
                        limit,
                      );
                    }}
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>
          )}
          {!!flowsError && (
            <div className="alert alert-danger">
              Error loading flows: {JSON.stringify(flowsError)}
            </div>
          )}
          {!flowHistoryData && isLoadingFlows && (
            <LoadingIndicator style={{ padding: '2rem' }} />
          )}
          {flowHistoryData ? (
            <CapacityComponentBulkHistory
              componentIds={null}
              data={flowHistoryData}
              limit={flowHistoryLimit}
              endGasDate={flowHistoryEndGasDate}
              startGasDate={flowHistoryStartGasDate || undefined}
              cycleColumnMode={flowHistoryCycleColumnMode}
              hasMoreHistory={!flowHistoryStartGasDate}
              isLoadingMoreHistory={isLoadingFlows}
              onLoadMoreHistory={loadOlderPathFlows}
              onSave={async () => {
                if (!flowHistoryPath) return;
                await loadPathFlows(
                  flowHistoryPath,
                  flowHistoryEndGasDate,
                  flowHistoryLimit,
                );
              }}
            />
          ) : null}
        </ReusableModal>
        <NoticeRecordModal
          noticeRecordId={selectedNoticeId}
          isOpen={selectedNoticeId !== null}
          onClose={() => setSelectedNoticeId(null)}
        />
      </div>
    );
  },
);
